/**
 * L8 — frontend mirror of `src/modules/wo-cost/cost.types.ts`.
 *
 * All money values are integer cents of a single ISO-4217 currency.
 * The UI converts to `currency / 100` only for display via
 * `formatMoney()`; the rest of the app keeps cents-int discipline.
 */

export interface WorkOrderCostPartLine {
  workOrderPartId: string;
  partId: string;
  partCode: string;
  partName: string;
  quantity: number;
  /** null when the line predates L8 or had no batch unit_cost. */
  unitCostCents: number | null;
  lineTotalCents: number | null;
}

export interface WorkOrderCostAssignment {
  assignmentId: string;
  userId: string;
  userDisplayName: string;
  role: string;
  hourlyRateCentsSnapshot: number | null;
  unassignedAt: string | null;
}

export interface WorkOrderCost {
  workOrderId: string;
  number: string;
  title: string;
  status: string;
  vesselId: string;
  companyId: string;
  currency: string;
  parts: {
    totalCents: number;
    countedLines: number;
    uncountedLines: number;
    lines: WorkOrderCostPartLine[];
  };
  labor: {
    totalCents: number;
    actualHours: number | null;
    averageHourlyRateCents: number | null;
    assignments: WorkOrderCostAssignment[];
  };
  totalCents: number;
}

export type CostGroupBy = 'month' | 'category' | 'vessel' | 'woType';

export interface CostBucket {
  key: string;
  label: string;
  partsCents: number;
  laborCents: number;
  totalCents: number;
  workOrderCount: number;
}

export interface CostAggregate {
  scope: 'vessel' | 'company';
  scopeId: string;
  from: string;
  to: string;
  groupBy: CostGroupBy;
  currency: string;
  buckets: CostBucket[];
  grandTotalCents: number;
}

/** Format `cents` as a localized money string (e.g. "1 234,56 €"). */
export function formatMoney(
  cents: number | null | undefined,
  currency: string,
  locale = 'fr-FR',
): string {
  if (cents === null || cents === undefined) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(cents / 100);
}
